import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChartOptions, ChartType, ChartDataset, Chart, Tooltip, CategoryScale, LinearScale, LineController, PointElement, LineElement, Legend, ChartData, BarController, BarElement } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Campaign } from '../../models/campaign';
import { RevenueData } from '../agent-dashboard/agent-dashboard.component';
import { VerticalChartType } from '../../models/verticalChartType';
import { Invoice } from '../../models/invoice';

@Component({
  selector: 'app-revenue-chart',
  imports: [BaseChartDirective],
  templateUrl: './revenue-chart.component.html',
  styleUrl: './revenue-chart.component.css'
})
export class RevenueChartComponent implements OnInit, OnChanges {
  @Input()
  campaigns: Campaign[] = [];

  @Input()
  invoices: Invoice[] = [];

  // Which series to plot - gross bookings ($) or campaign count
  @Input()
  metric: 'bookings' | 'count' = 'bookings';

  // Emitted when the user drills into a month (YYYY-MM) or returns to the monthly view (null)
  @Output()
  monthSelected = new EventEmitter<string | null>();

  // Emitted when the user picks a single day (YYYY-MM-DD) or clears the day selection (null)
  @Output()
  daySelected = new EventEmitter<string | null>();

  chartTitle: string = '';

  viewMode: 'month' | 'day' = 'month';
  selectedMonthKey: string | null = null;
  selectedMonthLabel: string = '';
  selectedDayKey: string | null = null;

  months: string[] = [];
  monthKeys: string[] = []; // Store YYYY-MM format for mapping
  days: string[] = [];
  dayKeys: string[] = [];   // Store YYYY-MM-DD format for mapping
  chartData!: ChartData;
  chartOptions!: ChartOptions;

  private readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Muted sage green bars - money, toned to sit with the page's dark background and amber axes
  private readonly bookingsColors = {
    bar: 'rgba(111, 174, 140, 0.75)',
    border: '#6fae8c',
    hover: '#8fd0ad',
    dim: 'rgba(111, 174, 140, 0.2)'
  };

  // Muted gold bars - campaign count, distinct from both the money green and the projected blue
  private readonly countColors = {
    bar: 'rgba(212, 160, 74, 0.75)',
    border: '#d4a04a',
    hover: '#e8bd74',
    dim: 'rgba(212, 160, 74, 0.2)'
  };

  private get barColor() { return this.metric === 'count' ? this.countColors.bar : this.bookingsColors.bar; }
  private get barBorderColor() { return this.metric === 'count' ? this.countColors.border : this.bookingsColors.border; }
  private get barHoverColor() { return this.metric === 'count' ? this.countColors.hover : this.bookingsColors.hover; }
  private get barDimColor() { return this.metric === 'count' ? this.countColors.dim : this.bookingsColors.dim; }

  // Projected-pace segment stacked on the current month - blue so it reads as "not booked yet"
  private readonly projectedColor = 'rgba(96, 165, 250, 0.35)';
  private readonly projectedBorderColor = '#60a5fa';

  // Pace-based projection for the current month, kept for the tooltip
  projectedMonthTotal: number = 0;

  private get seriesName() { return this.metric === 'count' ? 'Campaign Count' : 'Total Bookings'; }

  ngOnInit(): void {
    Chart.register(CategoryScale);
    Chart.register(LinearScale);
    Chart.register(LineController);
    Chart.register(BarElement);
    Chart.register(BarController);
    Chart.register(PointElement);
    Chart.register(LineElement);
    Chart.register(Tooltip);
    Chart.register(Legend);

    this.generateMonthLabels();
    this.initializeGraph();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.campaigns && this.campaigns.length > 0) {
      if (changes['campaigns'] || changes['metric']) {
        // New campaign data or a different metric - drop any drill-down and rebuild the monthly view
        this.viewMode = 'month';
        this.selectedMonthKey = null;
        this.selectedMonthLabel = '';
        this.selectedDayKey = null;
        this.days = [];
        this.dayKeys = [];
        this.generateMonthLabels();
        this.initializeGraph();
      }
    }
  }

  private generateMonthLabels() {
    // Find the earliest and latest dates from campaigns
    if (this.campaigns.length === 0) {
      this.months = [];
      this.monthKeys = [];
      return;
    }

    const dates = this.campaigns.map(c => new Date(c.createdDate));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

    // Ensure we include the current month
    const now = new Date();
    if (now > maxDate) {
      maxDate.setTime(now.getTime());
    }

    // Generate month labels from min to max
    this.months = [];
    this.monthKeys = [];

    const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

    while (current <= end) {
      const year = current.getFullYear();
      const month = current.getMonth();
      this.months.push(`${this.monthNames[month]} ${year}`);
      this.monthKeys.push(`${year}-${String(month + 1).padStart(2, '0')}`);
      current.setMonth(current.getMonth() + 1);
    }
  }

  // Build one label per day of the drilled-into month
  private generateDayLabels() {
    this.days = [];
    this.dayKeys = [];

    if (!this.selectedMonthKey) return;

    const year = Number(this.selectedMonthKey.substring(0, 4));
    const month = Number(this.selectedMonthKey.substring(5, 7)); // 1-based
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      this.days.push(String(day));
      this.dayKeys.push(`${this.selectedMonthKey}-${String(day).padStart(2, '0')}`);
    }
  }

  private initializeGraph() {
    if (this.viewMode === 'day' && this.selectedMonthKey) {
      this.renderDailyChart();
    } else {
      this.renderMonthlyChart();
    }
  }

  private renderMonthlyChart() {
    this.chartTitle = `${this.seriesName} - By Month`;

    const booked = this.getMonthlyValues();
    const projected = this.getMonthlyProjectedRemainder(booked);
    const hasProjection = projected.some(v => v > 0);

    const datasets: ChartDataset[] = [
      {
        label: hasProjection ? (this.metric === 'count' ? 'Actual' : 'Booked') : this.seriesName,
        data: booked,
        backgroundColor: this.barColor,
        borderColor: this.barBorderColor,
        borderWidth: 1,
        hoverBackgroundColor: this.barHoverColor,
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.9,
        categoryPercentage: 0.8
      }
    ];

    if (hasProjection) {
      datasets.push({
        label: 'Projected',
        data: projected,
        backgroundColor: this.projectedColor,
        borderColor: this.projectedBorderColor,
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(96, 165, 250, 0.55)',
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.9,
        categoryPercentage: 0.8
      });
    }

    this.chartData = {
      labels: this.months,
      datasets: datasets
    };

    this.chartOptions = this.buildChartOptions('', hasProjection);
  }

  private renderDailyChart() {
    this.chartTitle = `${this.seriesName} - By Day`;

    // Dim every other bar once a single day is selected
    const backgroundColors = this.dayKeys.map(key =>
      this.selectedDayKey === null || this.selectedDayKey === key
        ? this.barColor
        : this.barDimColor);

    this.chartData = {
      labels: this.days,
      datasets: [
        {
          label: this.seriesName,
          data: this.getDailyValues(),
          backgroundColor: backgroundColors,
          borderColor: this.barBorderColor,
          borderWidth: 1,
          hoverBackgroundColor: this.barHoverColor,
          type: 'bar',
          yAxisID: 'y',
          barPercentage: 0.9,
          categoryPercentage: 0.8
        }
      ]
    };

    this.chartOptions = this.buildChartOptions(this.selectedMonthLabel, false);
  }

  private buildChartOptions(xAxisTitle: string, stacked: boolean): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: true
      },
      onHover: (event, elements) => {
        const target = event?.native?.target as HTMLElement | null;
        if (target) {
          target.style.cursor = elements.length > 0 || this.viewMode === 'day' ? 'pointer' : 'default';
        }
      },
      plugins: {
        tooltip: {
          enabled: true,
          external: undefined,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          callbacks: {
            title: (context) => {
              const label = context[0].label || '';
              return this.viewMode === 'day' ? `${this.selectedMonthLabel} ${label}` : `${label}`;
            },
            label: (context) => {
              const datasetLabel = context.dataset.label || '';
              // The projected segment holds the remainder - show the full month pace instead
              const value = datasetLabel === 'Projected' ? this.projectedMonthTotal : context.parsed.y;
              const formatted = this.formatValue(value);
              return datasetLabel === 'Projected'
                ? `Projected month total: ${formatted}`
                : `${datasetLabel}: ${formatted}`;
            }
          }
        },
        legend: {
          display: stacked,
          position: 'top',
          align: 'center',
          labels: {
            color: '#ffaa33',
            usePointStyle: true,
            pointStyle: 'rect'
          }
        }
      },
      scales: {
        x: {
          stacked: stacked,
          beginAtZero: true,
          ticks: { color: '#ffaa33' },
          grid: { color: 'rgba(255, 170, 51, 0.15)' },
          title: {
            display: !!xAxisTitle,
            text: xAxisTitle,
            color: '#ffaa33'
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          stacked: stacked,
          beginAtZero: true,
          ticks: { color: '#ffaa33', precision: this.metric === 'count' ? 0 : undefined },
          grid: { color: 'rgba(255, 170, 51, 0.15)' },
          title: {
            display: true,
            text: this.metric === 'count' ? 'Campaigns' : 'Revenue ($)',
            color: '#ffaa33'
          }
        }
      },
    };
  }

  // Fired by the chart canvas - `active` is empty when the click missed every bar
  onChartClick(event: { event?: unknown, active?: object[] }) {
    const active = (event?.active ?? []) as { index?: number }[];
    const index = active.length > 0 ? active[0].index : undefined;

    if (this.viewMode === 'month') {
      if (index === undefined || index < 0 || index >= this.monthKeys.length) return;

      this.selectedMonthKey = this.monthKeys[index];
      this.selectedMonthLabel = this.months[index];
      this.selectedDayKey = null;
      this.viewMode = 'day';
      this.generateDayLabels();
      this.initializeGraph();
      this.monthSelected.emit(this.selectedMonthKey);
      return;
    }

    // Daily view: a bar filters to that day, anywhere else returns to the monthly view
    if (index !== undefined && index >= 0 && index < this.dayKeys.length) {
      const dayKey = this.dayKeys[index];
      this.selectedDayKey = this.selectedDayKey === dayKey ? null : dayKey;
      this.initializeGraph();
      this.daySelected.emit(this.selectedDayKey);
    } else {
      this.resetToMonthly();
    }
  }

  resetToMonthly() {
    this.viewMode = 'month';
    this.selectedMonthKey = null;
    this.selectedMonthLabel = '';
    this.selectedDayKey = null;
    this.days = [];
    this.dayKeys = [];
    this.initializeGraph();
    this.monthSelected.emit(null);
  }

  // Dollars or campaign count per month, depending on the selected metric
  getMonthlyValues(): number[] {
    return this.metric === 'count' ? this.getMonthlyCampaignCounts() : this.getMonthlyGrossBookings();
  }

  // Dollars or campaign count per day of the drilled-into month
  getDailyValues(): number[] {
    return this.metric === 'count' ? this.getDailyCampaignCounts() : this.getDailyGrossBookings();
  }

  formatValue(value: number): string {
    if (this.metric === 'count') {
      const rounded = Math.round(value);
      return `${rounded} ${rounded === 1 ? 'campaign' : 'campaigns'}`;
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  getMonthlyGrossBookings() : number[] {
    const monthlyRates: { [key: string]: number } = {};

    // Initialize all months to 0
    this.monthKeys.forEach(key => monthlyRates[key] = 0);

    // created dates are in format YYYY-MM-DD
    this.campaigns.forEach(c => {
      const yearMonth = c.createdDate.substring(0, 7); // Extract YYYY-MM
      if (monthlyRates.hasOwnProperty(yearMonth)) {
        monthlyRates[yearMonth] += c.rate;
      }
    });

    return this.monthKeys.map(key => monthlyRates[key]);
  }

  // What the current month would finish at if bookings kept the month's pace to date.
  // Returns the remainder to stack on top of the booked bar - every other month is 0.
  getMonthlyProjectedRemainder(booked: number[]): number[] {
    const remainder = this.monthKeys.map(() => 0);
    this.projectedMonthTotal = 0;

    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const index = this.monthKeys.indexOf(currentKey);
    if (index === -1) return remainder;

    // Pace is measured in weekdays (Mon-Fri) only - weekends are ignored
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    let weekdaysElapsed = 0;
    let weekdaysInMonth = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(now.getFullYear(), now.getMonth(), d).getDay();
      if (dow === 0 || dow === 6) continue;
      weekdaysInMonth++;
      if (d <= now.getDate()) weekdaysElapsed++;
    }
    const bookedSoFar = booked[index] ?? 0;

    // Nothing to project before the first weekday, after the last weekday, or before anything is booked
    if (bookedSoFar <= 0 || weekdaysElapsed === 0 || weekdaysElapsed >= weekdaysInMonth) return remainder;

    this.projectedMonthTotal = (bookedSoFar / weekdaysElapsed) * weekdaysInMonth;
    remainder[index] = this.projectedMonthTotal - bookedSoFar;

    return remainder;
  }

  getDailyGrossBookings(): number[] {
    const dailyRates: { [key: string]: number } = {};

    // Initialize every day of the selected month to 0
    this.dayKeys.forEach(key => dailyRates[key] = 0);

    // created dates are in format YYYY-MM-DD
    this.campaigns.forEach(c => {
      const day = c.createdDate.substring(0, 10); // Extract YYYY-MM-DD
      if (dailyRates.hasOwnProperty(day)) {
        dailyRates[day] += (Number(c.rate) || 0);
      }
    });

    return this.dayKeys.map(key => dailyRates[key]);
  }

  getDailyCampaignCounts(): number[] {
    const campaignsByDay: { [key: string]: number } = {};

    // Initialize every day of the selected month to 0
    this.dayKeys.forEach(key => campaignsByDay[key] = 0);

    // Count campaigns by their created date
    this.campaigns.forEach(c => {
      const day = c.createdDate.substring(0, 10); // Extract YYYY-MM-DD
      if (campaignsByDay.hasOwnProperty(day)) {
        campaignsByDay[day] += 1;
      }
    });

    return this.dayKeys.map(key => campaignsByDay[key]);
  }

  getMonthlyCampaignCounts(): number[] {
    const campaignsByMonth: { [key: string]: number } = {};

    // Initialize all months to 0
    this.monthKeys.forEach(key => campaignsByMonth[key] = 0);

    // Count campaigns by their created date month
    this.campaigns.forEach(c => {
      const yearMonth = c.createdDate.substring(0, 7); // Extract YYYY-MM
      if (campaignsByMonth.hasOwnProperty(yearMonth)) {
        campaignsByMonth[yearMonth] += 1;
      }
    });

    return this.monthKeys.map(key => campaignsByMonth[key]);
  }
}
